import { useUserIdWithLoading } from '@dreamstack/auth'
import { useLocale, useTranslation } from '@dreamstack/i18n'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { logger } from '@dreamstack/logger'
import { getPropertyLocationLabel } from '@dreamstack/real-estate-logic'
import { SimpleDefinitionList } from '@dreamstack/simple-components/src/lib/SimpleDefinitionList'
import { SimpleIconButton } from '@dreamstack/simple-components/src/lib/SimpleIconButton'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { ComponentProps, FunctionComponent } from 'react'
import { useFormatMinMax } from './helper/getMinMaxOfProperties'
import { useProjectBaseUrl } from './helper/useProjectBaseUrl'

const infoTitleClass =
  'font-semibold text-px-16 leading-px-24 lg:text-px-32 lg:leading-px-48'
const infoDescriptionClass =
  'text-px-13 leading-px-18 lg:text-px-16 lg:leading-px-24'

const InfoTitle = ({ className, ...rest }: ComponentProps<'div'>) => (
  <div className={cn(infoTitleClass, className)} {...rest} />
)

const InfoDescription = ({ className, ...rest }: ComponentProps<'div'>) => (
  <div className={cn(infoDescriptionClass, className)} {...rest} />
)

export type ProjectPreviewArgs = {
  id: number
  title: string | null
  city: string | null
  region: string | null
  titleImageUrl: string | null
  minMax: {
    minSqm: number
    maxSqm: number
    minPriceRaw: number
    maxPriceRaw: number
    minRooms: number
    maxRooms: number
  }
}

export const ProjectPreview: FunctionComponent<
  React.PropsWithChildren<{ project: ProjectPreviewArgs }>
> = ({ project }) => {
  const t = useTranslation()
  const locale = useLocale()
  const projectBaseUrl = useProjectBaseUrl()
  const { userId } = useUserIdWithLoading()
  const router = useRouter()
  const { mutateAsync: trackButtonClickServer } =
    trpc.anonymousTracking.trackEvent.useMutation()

  if (!project.minMax) {
    logger.error('minMax is undefined', project)
  }

  const { minPrice, rooms, sqm } = useFormatMinMax(project.minMax)
  const countRooms = parseInt(rooms)

  const projectSubtitle = getPropertyLocationLabel(project)

  const trackClickCallToActionButton = async () => {
    const anonymousUserId = getOrCreateAnonymousId()
    await trackButtonClickServer({
      eventName: 'buttonClick',
      userId,
      anonymousUserId,
      data: {
        buttonAction: 'Coming Soon Projekt',
        url: router.asPath,
        additionalInfo: {
          projectId: project.id,
        },
      },
    })
  }

  const filteredHref = `${projectBaseUrl}/${project.id}`
  return (
    <div>
      <Link
        aria-label="View Project"
        onClick={trackClickCallToActionButton}
        onKeyDown={trackClickCallToActionButton} // Add keyboard event
        role="button" // Add role attribute
        tabIndex={0} // Make it focusable
        href={filteredHref}
        passHref
        data-cy={'ProjectPreviewLinkToProject'}
      >
        <div className="aspect-[3/2] relative self-center cursor-pointer">
          <Image
            className="object-cover"
            fill
            src={
              project.titleImageUrl ??
              `/static/images/image-placeholder-small${locale.toUpperCase()}.png`
            }
            alt={'Project title image'}
          />
        </div>
        <div
          className={cn(
            'cursor-pointer bg-accentroGray-full text-accentroWhite-full relative p-px-16 mr-px-48',
            'md:px-px-24 md:py-px-32 md:ml-px-24 md:mr-px-40 md:-mt-px-48',
            'lg:px-px-48 lg:ml-px-48 lg:mr-px-80 lg:-mt-px-96'
          )}
        >
          <InfoTitle className="pr-px-48 md:pr-px-64">
            {project.title}
          </InfoTitle>
          <InfoDescription className="mb-px-24">
            {projectSubtitle}
          </InfoDescription>

          <div className="flex space-x-px-24 lg:space-x-px-48">
            <div className="flex flex-1 flex-wrap justify-between items-center gap-px-16">
              {rooms && (
                <SimpleDefinitionList
                  dt={
                    <InfoTitle className="whitespace-nowrap">{rooms}</InfoTitle>
                  }
                  dd={
                    <InfoDescription>
                      {t('accentro:room.rooms', { count: countRooms })}
                    </InfoDescription>
                  }
                />
              )}
              {sqm && (
                <SimpleDefinitionList
                  dt={
                    <InfoTitle className="whitespace-nowrap">{`${sqm}`}</InfoTitle>
                  }
                  dd={
                    <InfoDescription>
                      {t('accentro:livingSpace')}
                    </InfoDescription>
                  }
                />
              )}
              {minPrice && (
                <SimpleDefinitionList
                  dt={
                    <InfoTitle className="whitespace-nowrap">
                      {`${t('accentro:filter.from')} ${minPrice} `}
                      <InfoDescription className="inline">EUR</InfoDescription>
                    </InfoTitle>
                  }
                  dd={
                    <InfoDescription>
                      {t('accentro:purchase-price')}
                    </InfoDescription>
                  }
                />
              )}
            </div>
            <div className="flex items-end">
              <SimpleIconButton color="inherit" aria-label="View Project">
                <ArrowRight className="text-accentroWhite-full h-px-24 w-px-24 lg:h-px-48 lg:w-px-48" />
              </SimpleIconButton>
            </div>
          </div>
        </div>
      </Link>
    </div>
  )
}
